import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Award, Handshake, Wrench } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { IMG, LOGOS, PARTS } from "@/lib/images";
import { Corner, FadeIn, MaskLines, SectionHead } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { PrimaryBtn, GhostBtn } from "@/components/Buttons";
import CtaBand from "@/components/CtaBand";

const STRENGTH_ICONS = [Award, Wrench, Handshake];
const BUY_IMAGES = [
  PARTS.cracked,
  PARTS.motherboard,
  PARTS.cracked2,
  PARTS.blocked,
  PARTS.flex,
  PARTS.parts,
];

export default function Home() {
  const { t } = useLang();
  const c = t.home;
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 70]);

  return (
    <div>
      {/* ---------------- HERO ---------------- */}
      <section ref={heroRef} className="relative overflow-hidden pt-10" data-testid="hero">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(55%_60%_at_75%_0%,rgba(14,124,90,0.09),transparent_70%)]" />
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(22,36,29,0.045)_1px,transparent_1px),linear-gradient(90deg,rgba(22,36,29,0.045)_1px,transparent_1px)] bg-[size:56px_56px] [mask-image:radial-gradient(70%_60%_at_50%_0%,black,transparent)]" />

        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 pb-24 pt-10 sm:px-8 lg:grid-cols-12 lg:gap-12 lg:pb-32 lg:pt-14">
          <div className="lg:col-span-7">
            <FadeIn y={14} delay={0.05}>
              <span className="inline-flex items-center gap-2.5 border border-brand/30 bg-white px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-branddark">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                {c.eyebrow}
              </span>
            </FadeIn>

            <MaskLines
              lines={c.titleLines}
              accent={1}
              className="mt-8 font-display text-4xl font-bold tracking-[-0.02em] sm:text-5xl lg:text-[3.4rem] lg:leading-[1.12]"
            />

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.7 }}
              className="mt-7 max-w-xl text-base font-medium leading-relaxed text-ink2 sm:text-lg"
            >
              {c.sub}
            </motion.p>

            <FadeIn y={14} delay={0.7} className="mt-10 flex flex-wrap items-center gap-4">
              <PrimaryBtn to="/contact" testid="hero-cta-lot">
                {c.ctaLot}
              </PrimaryBtn>
              <GhostBtn to="/nos-activites" testid="hero-cta-activities">
                {c.ctaActivities}
              </GhostBtn>
            </FadeIn>

            <FadeIn y={14} delay={0.85} className="mt-12">
              <div className="flex items-center gap-3 border-t border-line pt-6 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-ink3">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                {c.ticker}
              </div>
            </FadeIn>
          </div>

          <div className="lg:col-span-5">
            <motion.div style={{ y: yImg }} className="relative">
              <Corner pos="tl" />
              <Corner pos="tr" />
              <Corner pos="bl" />
              <Corner pos="br" />
              <div className="relative overflow-hidden border border-line shadow-card">
                <img
                  src={IMG.hero}
                  alt="Smartphones et composants électroniques"
                  className="h-[380px] w-full object-cover sm:h-[460px] lg:h-[540px]"
                  loading="eager"
                />
                <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/45 to-transparent" />
                <div className="absolute bottom-5 left-5 border border-line bg-white/95 px-4 py-3 backdrop-blur">
                  <span className="block font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-brand">
                    {c.tagActive}
                  </span>
                  <span className="mt-1 block font-display text-sm font-bold text-ink">
                    {c.tagLot}
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee items={c.marquee} />

      {/* ---------------- CE QUE NOUS RACHETONS ---------------- */}
      <section className="py-24 lg:py-32" data-testid="section-buy">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHead num={c.buy.num} eyebrow={c.buy.eyebrow} title={c.buy.title} sub={c.buy.sub} />

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {c.buy.items.map((item, i) => (
              <FadeIn key={i} delay={0.05 * (i % 3)}>
                <div
                  className="group border border-line bg-white transition-all duration-300 hover:border-brand/60 hover:shadow-cardhover"
                  data-testid={`buy-card-${i}`}
                >
                  <div className="h-48 overflow-hidden bg-paper">
                    <img
                      src={BUY_IMAGES[i]}
                      alt={item.title}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex items-center justify-between border-t border-line px-6 py-4">
                    <p className="font-display text-lg font-bold tracking-[-0.01em] text-ink">
                      {item.title}
                    </p>
                    <span className="font-mono text-xs font-bold text-brand">
                      /{String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={0.15}>
            <div className="mt-10 flex flex-col items-start justify-between gap-6 border-l-4 border-brand bg-brandtint/50 p-8 sm:flex-row sm:items-center lg:p-10">
              <p className="max-w-xl font-display text-xl font-bold tracking-[-0.01em] text-ink sm:text-2xl">
                {c.buy.cta}
              </p>
              <PrimaryBtn to="/contact" testid="buy-cta-lot">
                {c.buy.ctaBtn}
              </PrimaryBtn>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ---------------- NOS POINTS FORTS ---------------- */}
      <section className="border-t border-line bg-white py-24 lg:py-32" data-testid="section-strengths">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHead num={c.strengths.num} eyebrow={c.strengths.eyebrow} title={c.strengths.title} />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {c.strengths.items.map((s, i) => {
              const Icon = STRENGTH_ICONS[i];
              return (
                <FadeIn key={i} delay={0.08 * i}>
                  <div className="h-full border border-line bg-paper p-8 transition-all duration-300 hover:-translate-y-1 hover:border-brand/50 hover:shadow-card">
                    <div className="flex h-12 w-12 items-center justify-center bg-brand text-white">
                      <Icon className="h-5 w-5" />
                    </div>
                    <p className="mt-7 font-display text-3xl font-bold tracking-[-0.02em] text-ink">
                      {s.stat}
                    </p>
                    <p className="mt-3 text-sm font-medium leading-relaxed text-ink2">{s.label}</p>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------------- FILIÈRES ENVIRONNEMENTALES ---------------- */}
      <section className="border-t border-line py-24 lg:py-32" data-testid="section-env">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHead num={c.env.num} eyebrow={c.env.eyebrow} title={c.env.title} sub={c.env.body} />
          <FadeIn delay={0.1}>
            <div className="mt-12 grid gap-6 sm:grid-cols-3">
              {c.env.partners.map((p) => (
                <div
                  key={p}
                  className="flex h-28 items-center justify-center border border-line bg-white p-6 shadow-sm transition-shadow duration-300 hover:shadow-card"
                  data-testid={`env-logo-${p.toLowerCase()}`}
                >
                  <img
                    src={LOGOS[p.toLowerCase()]}
                    alt={p}
                    className="max-h-16 w-auto object-contain"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      <CtaBand title={c.cta.title} testid="home-cta-band-lot" />
    </div>
  );
}
